import type { MapDocument } from "../document/types.ts";
import { frameId } from "../assets/manifest.ts";
import { propFeet } from "../editor/objects.ts";
import type { ChunkMark } from "./chunk.ts";
import type { CompileContext } from "./context.ts";

/**
 * The mark pass: the props of a map whose frame the game marks (`CompileContext.markedFrames`), listed into the client
 * and server chunk that holds each with an index of its own. A game marks frames to give the props made of them a
 * meaning Daedalus does not read; the compile only numbers them. A mark is a string the game chooses; the index is the
 * prop's place among the map's marked props sorted by authored id, a number a reader holds in two bytes and that does
 * not move with the order the props were placed in. A prop carrying a behaviour is a thing, not a marked prop.
 */

/**
 * The most marked props one chunk lists.
 */
export const MAX_CHUNK_MARKS = 1024;

/**
 * The most marked props one map holds: the index is held in two bytes.
 */
export const MAX_MAP_MARKS = 65_535;

const SIXTEENTHS = 16;

/**
 * A map's marked props in index order, each with the chunk column and row the prop belongs to.
 */
export interface MapMarks {
  readonly mapId: string;
  readonly entries: readonly { readonly mark: ChunkMark; readonly column: number; readonly row: number }[];
}

/**
 * The order a map's marked props are numbered in: their authored ids sorted by UTF-16 code unit.
 *
 * @param ids The authored ids.
 * @returns The ids in index order, a fresh list.
 */
export function markedPropOrder(ids: readonly string[]): string[] {
  return [...ids].sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));
}

/**
 * Finds a map's marked props and numbers them.
 *
 * @param map The map as authored.
 * @param context The compile context, whose `markedFrames` says which frames are marked and with what, and whose
 *   `frameSizes` give a frame's width for the feet point (a frame of unknown width is taken as one tile wide).
 * @param tileSize Tile size in pixels.
 * @returns The map's marks, or null when the context marks no frame or the map holds no marked prop.
 * @throws Error naming the prop when an authored id is held twice among the marked props, or the map when it holds more
 *   than `MAX_MAP_MARKS`.
 */
export function mapMarks(map: MapDocument, context: CompileContext, tileSize: number): MapMarks | null {
  const frames = context.markedFrames;
  if (frames === undefined) {
    return null;
  }
  const found: { readonly id: string; readonly mark: string; readonly x: number; readonly y: number; readonly column: number; readonly row: number }[] = [];
  for (const layer of map.layers) {
    if (layer.kind !== "prop") {
      continue;
    }
    for (const prop of layer.props) {
      if (prop.behaviour !== undefined) {
        continue;
      }
      const id = frameId(prop.frame.tileset, null, prop.frame.tile);
      const mark = frames[id];
      if (mark === undefined) {
        continue;
      }
      const feet = propFeet(prop, context.frameSizes?.[id]?.w ?? tileSize, tileSize);
      found.push({ id: prop.id, mark, x: Math.round(feet.x * SIXTEENTHS), y: Math.round(feet.y * SIXTEENTHS), column: Math.floor(prop.x), row: Math.floor(prop.y) });
    }
  }
  if (found.length === 0) {
    return null;
  }
  if (found.length > MAX_MAP_MARKS) {
    throw new Error(`map ${map.id} holds ${found.length} marked props, at most ${MAX_MAP_MARKS}`);
  }
  found.sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  const entries = found.map((entry, n) => {
    if (n > 0 && (found[n - 1] as { readonly id: string }).id === entry.id) {
      throw new Error(`prop ${entry.id} is held twice among the marked props of map ${map.id}`);
    }
    return { mark: { id: entry.id, n, mark: entry.mark, x: entry.x, y: entry.y }, column: entry.column, row: entry.row };
  });
  return { mapId: map.id, entries };
}

/**
 * Buckets a map's marks by the chunk that holds each prop (the chunk of the floor of its placement, which is where the
 * chunk's prop layers hold it).
 *
 * @param marks The map's marks, or null for none.
 * @param chunkSize Chunk size in tiles.
 * @param columns The map's chunk columns.
 * @returns The marks of each chunk that has any, by `cy * columns + cx`, in index order.
 * @throws Error naming the chunk and map when a chunk holds more than `MAX_CHUNK_MARKS`.
 */
export function chunkMarkTable(marks: MapMarks | null, chunkSize: number, columns: number): ReadonlyMap<number, readonly ChunkMark[]> {
  const table = new Map<number, ChunkMark[]>();
  if (marks === null) {
    return table;
  }
  for (const entry of marks.entries) {
    const cx = Math.floor(entry.column / chunkSize);
    const cy = Math.floor(entry.row / chunkSize);
    const key = cy * columns + cx;
    const list = table.get(key) ?? [];
    list.push(entry.mark);
    table.set(key, list);
    if (list.length > MAX_CHUNK_MARKS) {
      throw new Error(`chunk ${cx},${cy} of map ${marks.mapId} holds ${list.length} marked props, at most ${MAX_CHUNK_MARKS}`);
    }
  }
  return table;
}
